import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type {
	CreateReviewTemplateRequestInput,
	GetReviewTemplatesResponse,
	UpdateReviewTemplateRequestInput,
} from "@performyard/service-contracts";

import request from "../../services/defs/internal-api/internal-base-request";
import {
	createReviewTemplateV2,
	deleteReviewTemplateV2,
	fetchReviewTemplateV2,
	updateReviewTemplateV2,
} from "../../services/defs/review-templates";

export const useReviewTemplates = () => {
	return useQuery({
		queryKey: ["review-templates"],
		queryFn: async () => {
			const [response] = await request<GetReviewTemplatesResponse>("/v1/reviews/review-templates");
			return response.data;
		},
		meta: { errorMessage: "There was an error loading review templates." },
	});
};

export const useReviewTemplate = (templateId: string | null) => {
	return useQuery({
		queryKey: ["review-template-v2", templateId],
		queryFn: () => {
			if (!templateId) throw new Error("templateId is required");
			return fetchReviewTemplateV2(templateId);
		},
		enabled: Boolean(templateId),
		meta: { errorMessage: "There was an error loading the review template." },
	});
};

export const useCreateReviewTemplate = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationKey: ["create-review-template"],
		mutationFn: (data: CreateReviewTemplateRequestInput) => createReviewTemplateV2(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["review-templates"] });
		},
		meta: { errorMessage: "There was an error creating the review template." },
	});
};

export const useUpdateReviewTemplate = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationKey: ["update-review-template"],
		mutationFn: ({ templateId, data }: { templateId: string; data: UpdateReviewTemplateRequestInput }) =>
			updateReviewTemplateV2(templateId, data),
		onSuccess: (_result, { templateId }) => {
			queryClient.invalidateQueries({ queryKey: ["review-templates"] });
			queryClient.invalidateQueries({ queryKey: ["review-template-v2", templateId] });
		},
		meta: { errorMessage: "There was an error saving the review template." },
	});
};

export const useDeleteReviewTemplate = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationKey: ["delete-review-template"],
		mutationFn: (templateId: string) => deleteReviewTemplateV2(templateId),
		onSuccess: (_result, templateId) => {
			queryClient.invalidateQueries({ queryKey: ["review-templates"] });
			queryClient.removeQueries({ queryKey: ["review-template-v2", templateId] });
		},
		meta: { errorMessage: "There was an error deleting the review template." },
	});
};
